import { FC, useContext, useState } from "react";
import { useMediaQuery } from "@mui/material";
import Table from "../../../../components/Table";
import { UserAdminPayoutsResult } from "../../../../types/User";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../../contexts/AuthContext";
import { ArrowDownIcon, InfoIcon } from "../../../../assets/img/icons";
import IconButton from "../../../../components/IconButton";
import MemberDetails, {
    MemberDetailsUser,
} from "../../../Members/MemberDetails";
import { PayoutsAdminSortBy } from "../../../../types/api/admin/payouts";

import styles from "./styles.module.scss";
import OpenInNewTabLink from "../../../../components/OpenInNewTabLink";
import { getUserName } from "../../../../utils/string";

type Props = {
    isLoading: boolean;
    users: UserAdminPayoutsResult[];
    userByPage: number;
    page: number;
    /**
     * Snapshot des filtres de la page Admin/Payouts pour pouvoir les restaurer
     * quand on revient via "Retourner à la recherche".
     */
    returnFilters?: {
        searchInput: string;
        startDate: string;
        endDate: string;
        showUserWithNoPayouts: boolean;
        fundingStatusSort: string;
        sortBy: PayoutsAdminSortBy;
        sortOrder: "asc" | "desc";
        page: number;
        userByPage: number;
    };
    fundingStatusMap?: Record<
        string,
        { isFunded: boolean; fundedTotalBalance?: string } | null
    >;
    showFundingColumn?: boolean;
    sortBy: string;
    sortOrder: string;
    onSortByPayoutDesc?: () => void;
    onSortByCapital?: (order: "asc" | "desc") => void;
    onEditProfile?: (user: UserAdminPayoutsResult) => void;
};

const PayoutsTable: FC<Props> = ({
    isLoading,
    users,
    userByPage,
    page,
    returnFilters,
    fundingStatusMap = {},
    showFundingColumn = false,
    sortBy,
    sortOrder,
    onSortByPayoutDesc,
    onSortByCapital,
    onEditProfile,
}) => {
    const navigate = useNavigate();
    const { hasPermission, isStreamOn } = useContext(AuthContext)!;
    const isMobile = useMediaQuery("(max-width: 1050px)");
    const [notesUser, setNotesUser] = useState<MemberDetailsUser | null>(null);

    const handleSeePayouts = (user: UserAdminPayoutsResult) => {
        // Stocker l'URL actuelle avec tous les filtres pour pouvoir y revenir
        const currentUrl = window.location.pathname + window.location.search;
        if (returnFilters) {
            sessionStorage.setItem(
                "adminPayoutsReturnFilters",
                JSON.stringify(returnFilters)
            );
        }
        sessionStorage.setItem("returnToSearchUrl", currentUrl);
        navigate(`/payouts?userId=${user.id}`);
    };

    const handleSeeTrades = (user: UserAdminPayoutsResult) => {
        const currentUrl = window.location.pathname + window.location.search;
        if (returnFilters) {
            sessionStorage.setItem(
                "adminPayoutsReturnFilters",
                JSON.stringify(returnFilters)
            );
        }
        sessionStorage.setItem("returnToSearchUrl", currentUrl);
        navigate(`/trades?userId=${user.id}`);
    };

    const getOptions = (user: UserAdminPayoutsResult) => {
        const options = [];

        if (hasPermission("admin-payouts", "read")) {
            options.push({
                label: "Voir les payouts",
                onClick: () => handleSeePayouts(user),
            });
        }
        if (hasPermission("admin-trading-sessions", "read")) {
            options.push({
                label: "Voir les performances",
                onClick: () => handleSeeTrades(user),
            });
        }
        if (hasPermission("admin-members", "update") && onEditProfile) {
            options.push({
                label: "Éditer le profil",
                onClick: () => onEditProfile(user),
            });
        }

        return options;
    };

    const renderFundingStatus = (userId: string) => {
        const status = fundingStatusMap[userId];
        if (status === undefined) {
            return (
                <span className={styles.fundingStatus_loading}>—</span>
            );
        }
        if (status === null) {
            return (
                <span className={styles.fundingStatus_pillNeutral}>
                    Non renseigné
                </span>
            );
        }
        return (
            <span
                className={
                    status.isFunded
                        ? styles.fundingStatus_pillFunded
                        : styles.fundingStatus_pillNotFunded
                }
            >
                {status.isFunded ? "Financé" : "Non financé"}
            </span>
        );
    };

    const renderCapitaleSousGestion = (userId: string) => {
        const status = fundingStatusMap[userId];
        if (status === undefined) {
            return (
                <span className={styles.capitaleSousGestion_cell}>
                    <span className={styles.fundingStatus_loading}>—</span>
                </span>
            );
        }
        if (
            status === null ||
            !status.fundedTotalBalance ||
            status.fundedTotalBalance === ""
        ) {
            return (
                <span className={styles.capitaleSousGestion_cell}>
                    <span className={styles.fundingStatus_loading}>—</span>
                </span>
            );
        }
        return (
            <span className={styles.capitaleSousGestion_cell}>
                <span className={styles.capitaleSousGestion_amount}>
                    {formatAmount(Number(status.fundedTotalBalance))} $
                </span>
            </span>
        );
    };

    const showRanking = sortBy === "payout_total" && sortOrder === "desc";
    const isPayoutDesc = sortBy === "payout_total" && sortOrder === "desc";
    const isCapitalSort = sortBy === "fundedTotalBalance";

    const formatAmount = (value: number) =>
        new Intl.NumberFormat("fr-FR", {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
        }).format(value);

    const formatUsers = (users: UserAdminPayoutsResult[]) =>
        users.map((user, key) => ({
            ...user,
            ...(showRanking && {
                key: (
                    <span className={styles.admin_columns_key}>
                        #{(page - 1) * userByPage + (key + 1)}
                    </span>
                ),
            }),
            pseudo: (
                <span className={styles.name_value}>
                    <span
                        className={styles.notesIcon}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <IconButton
                            size="medium"
                            icon={InfoIcon}
                            onClick={() =>
                                setNotesUser({
                                    id: user.id,
                                    firstname: user.firstname,
                                    lastname: user.lastname,
                                    pseudo: user.pseudo,
                                })
                            }
                        />
                    </span>
                    <span className={styles.name_text}>
                        {getUserName(
                            user.firstname,
                            user.lastname,
                            user.pseudo,
                            isStreamOn
                        )}
                    </span>
                    <OpenInNewTabLink
                        href={`/payouts?userId=${encodeURIComponent(user.id)}`}
                    />
                </span>
            ),
            ...(showFundingColumn && {
                funding_status:
                    isMobile
                        ? (
                              <span
                                  className={`${styles.result} ${
                                      user.payout_total > 0
                                          ? styles.dcr_green
                                          : user.payout_total < 0
                                          ? styles.dcr_red
                                          : ""
                                  }`}
                              >
                                  {formatAmount(user.payout_total)} $
                              </span>
                          )
                        : renderFundingStatus(user.id),
                capitale_sous_gestion: renderCapitaleSousGestion(user.id),
            }),
            payout_total: (
                isMobile && showFundingColumn ? (
                    renderFundingStatus(user.id)
                ) : (
                    <span
                        className={`${styles.result} ${
                            user.payout_total > 0
                                ? styles.dcr_green
                                : user.payout_total < 0
                                ? styles.dcr_red
                                : ""
                        }`}
                    >
                        {formatAmount(user.payout_total)} $
                    </span>
                )
            ),
            options: getOptions(user),
            onClick: () => handleSeePayouts(user),
        }));

    return (
        <>
            <Table
                className={styles.table}
                loading={isLoading}
                tableLayout={"fixed"}
                rows={[
                    ...(showRanking
                        ? [{ label: "", value: "key", width: 42 }]
                        : []),
                    {
                        label: (
                            <div className={styles.name_key}>
                                NOM
                            </div>
                        ),
                        value: "pseudo",
                        width: 180,
                    },
                    ...(showFundingColumn
                        ? [
                              {
                                  label: isMobile
                                      ? "Payouts"
                                      : "Statut financé",
                                  value: "funding_status",
                                  width: 140,
                              },
                              {
                                  label: (
                                      <div className={styles.capital_header_content}>
                                          CAPITAL
                                          {isCapitalSort && (
                                              <span className={styles.payout_sort_icon}>
                                                  <ArrowDownIcon
                                                      width={12}
                                                      style={{
                                                          transform:
                                                              sortOrder === "asc"
                                                                  ? "rotate(180deg)"
                                                                  : undefined,
                                                      }}
                                                  />
                                              </span>
                                          )}
                                      </div>
                                  ),
                                  value: "capitale_sous_gestion",
                                  width: 140,
                                  className: onSortByCapital
                                      ? `${styles.capitaleSousGestion_header} ${styles.capitalHeaderClickable}`
                                      : styles.capitaleSousGestion_header,
                                  onClick: onSortByCapital
                                      ? () =>
                                            onSortByCapital(
                                                isCapitalSort && sortOrder === "desc"
                                                    ? "asc"
                                                    : "desc"
                                            )
                                      : undefined,
                              },
                          ]
                        : []),
                    {
                        label:
                            isMobile && showFundingColumn ? (
                                "STATUT FINANCÉ"
                            ) : (
                                <div className={styles.payout_key}>
                                    PAYOUT{" "}
                                    {isPayoutDesc && (
                                        <span className={styles.payout_sort_icon}>
                                            <ArrowDownIcon width={12} />
                                        </span>
                                    )}
                                </div>
                            ),
                        value: "payout_total",
                        width: 130,
                        onClick:
                            isMobile && showFundingColumn
                                ? undefined
                                : onSortByPayoutDesc,
                        className:
                            isMobile && showFundingColumn
                                ? undefined
                                : onSortByPayoutDesc
                                ? styles.payoutHeaderClickable
                                : undefined,
                    },
                ]}
                data={formatUsers(users)}
            />
            {notesUser && (
                <MemberDetails
                    open
                    handleClose={() => setNotesUser(null)}
                    user={notesUser}
                />
            )}
        </>
    );
};

export default PayoutsTable;
